import { Api, ApiError, type Session } from "../api/client.ts";

/**
 * Sign in, or create an account.
 *
 * A hand-built overlay rather than a native `<dialog>`. `<dialog>` would give
 * focus trapping, Escape to close, an inert background and the top layer for
 * free, and normally that beats reimplementing all four — but Bitwarden's
 * autofill has open bugs against `showModal()` (it throws trying to hide its
 * own popover, and on Chrome disables itself entirely with a warning that the
 * page is "interfering with the Bitwarden experience"; see
 * github.com/bitwarden/clients issues #21388, #6168, #13603, #16359). A
 * password manager failing on the sign-in form is worse than doing focus
 * trapping by hand, so this trades the free behaviour for compatibility.
 *
 * The form is deliberately plain. Nothing here validates the password beyond
 * what the server requires, and nothing here decides what the account may do —
 * both belong on the server, and duplicating either would let the two disagree.
 */
export class AuthDialog {
  private readonly overlay: HTMLElement;
  private readonly card: HTMLElement;
  private readonly choice: HTMLElement;
  private readonly choicePanels: HTMLElement;
  private readonly choiceActions: HTMLElement;
  private readonly choiceCancel: HTMLButtonElement;
  private readonly form: HTMLFormElement;
  private readonly email: HTMLInputElement;
  private readonly password: HTMLInputElement;
  private readonly registerFields: HTMLElement[];
  private readonly firstName: HTMLInputElement;
  private readonly lastName: HTMLInputElement;
  private readonly company: HTMLInputElement;
  private readonly repeatPassword: HTMLInputElement;
  private readonly error: HTMLElement;
  private readonly submit: HTMLButtonElement;
  private readonly title: HTMLElement;
  private readonly back: HTMLButtonElement;
  private readonly cancel: HTMLButtonElement;
  private readonly hint: HTMLElement;
  private readonly pendingPanel: HTMLElement;
  private readonly pendingPanelMessage: HTMLElement;
  private readonly pendingPanelClose: HTMLButtonElement;
  private readonly forgotLink: HTMLButtonElement;
  private readonly forgotForm: HTMLFormElement;
  private readonly forgotEmail: HTMLInputElement;
  private readonly forgotError: HTMLElement;
  private readonly forgotBack: HTMLButtonElement;
  private readonly forgotCancel: HTMLButtonElement;
  private readonly forgotSubmit: HTMLButtonElement;
  private readonly forgotSentPanel: HTMLElement;
  private readonly forgotSentMessage: HTMLElement;
  private readonly forgotSentClose: HTMLButtonElement;

  private mode: "login" | "register" = "login";
  private step: "choice" | "form" | "pending" | "forgot" | "forgot-sent" = "choice";
  private pendingMessage = "Sign in to start splitting.";
  /** Whether the form now on screen has a "Back" to the choice screen — remembered so "Lost password?" can return to the same place. */
  private formCameFromChoice = false;
  /** Whether this open() can be dismissed without signing in — see `open()`. */
  private dismissable = true;
  private resolve: ((result: Session | "signup" | "pending-verification" | null) => void) | null = null;
  private previouslyFocused: HTMLElement | null = null;

  constructor(
    private readonly api: Api,
    private readonly root: Document = document,
  ) {
    this.overlay = root.createElement("div");
    this.overlay.className = "auth-overlay";
    this.overlay.hidden = true;
    this.overlay.innerHTML = `
      <div class="auth" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <div class="auth__body">
          <h2 class="auth__title" id="auth-title">Sign in</h2>
          <p class="auth__hint">Your drawing never leaves this machine.</p>

          <div class="auth__choice">
            <div class="auth__choice-panels">
              <button type="button" class="auth__choice-panel" data-choice="register">
                <span class="auth__choice-heading">Create an account</span>
                <span class="auth__choice-sub">Free, or choose a paid plan.</span>
              </button>
              <button type="button" class="auth__choice-panel" data-choice="login">
                <span class="auth__choice-heading">Sign in</span>
                <span class="auth__choice-sub">Already have an account?</span>
              </button>
            </div>
            <div class="auth__actions auth__choice-actions">
              <span class="auth__spacer"></span>
              <button type="button" class="btn auth__choice-cancel">Cancel</button>
            </div>
          </div>

          <form class="auth__form" novalidate hidden>
            <label class="auth__field">
              <span>Email</span>
              <input type="email" name="email" autocomplete="username" required />
            </label>
            <div class="auth__row auth__register-field">
              <label class="auth__field">
                <span>First name</span>
                <input type="text" name="firstName" autocomplete="given-name" />
              </label>
              <label class="auth__field">
                <span>Last name</span>
                <input type="text" name="lastName" autocomplete="family-name" />
              </label>
            </div>
            <label class="auth__field auth__register-field">
              <span>Company <span class="auth__optional">(optional)</span></span>
              <input type="text" name="company" autocomplete="organization" />
            </label>
            <label class="auth__field">
              <span>Password</span>
              <input type="password" name="password" autocomplete="current-password" required minlength="12" />
            </label>
            <label class="auth__field auth__register-field">
              <span>Repeat password</span>
              <input type="password" name="repeatPassword" autocomplete="new-password" minlength="12" />
            </label>
            <p class="auth__error" hidden></p>
            <div class="auth__actions">
              <button type="button" class="linkbtn auth__back">&larr; Back</button>
              <button type="button" class="linkbtn auth__forgot-link">Lost password?</button>
              <span class="auth__spacer"></span>
              <button type="button" class="btn auth__cancel">Cancel</button>
              <button type="submit" class="btn btn--primary auth__submit">Sign in</button>
            </div>
          </form>

          <div class="auth__pending" hidden>
            <p class="auth__pending-message"></p>
            <p class="auth__pending-note">You will not be able to sign in until your email address has been verified.</p>
            <p class="auth__pending-spam">Can't find it? Check your spam or junk folder.</p>
            <div class="auth__actions">
              <span class="auth__spacer"></span>
              <button type="button" class="btn btn--primary auth__pending-close">Done</button>
            </div>
          </div>

          <form class="auth__forgot" novalidate hidden>
            <p class="auth__hint">Enter the email you sign in with and we'll send you a link to reset your password.</p>
            <label class="auth__field">
              <span>Email</span>
              <input type="email" name="forgotEmail" autocomplete="username" required />
            </label>
            <p class="auth__error auth__forgot-error" hidden></p>
            <div class="auth__actions">
              <button type="button" class="linkbtn auth__forgot-back">&larr; Back</button>
              <span class="auth__spacer"></span>
              <button type="button" class="btn auth__forgot-cancel">Cancel</button>
              <button type="submit" class="btn btn--primary auth__forgot-submit">Send reset link</button>
            </div>
          </form>

          <div class="auth__forgot-sent" hidden>
            <p class="auth__forgot-sent-message"></p>
            <div class="auth__actions">
              <span class="auth__spacer"></span>
              <button type="button" class="btn btn--primary auth__forgot-sent-close">Done</button>
            </div>
          </div>
        </div>
      </div>
    `;
    root.body.append(this.overlay);

    this.card = this.overlay.querySelector(".auth")!;
    this.choice = this.overlay.querySelector(".auth__choice")!;
    this.choicePanels = this.overlay.querySelector(".auth__choice-panels")!;
    this.choiceActions = this.overlay.querySelector(".auth__choice-actions")!;
    this.choiceCancel = this.overlay.querySelector(".auth__choice-cancel")!;
    this.form = this.overlay.querySelector(".auth__form")!;
    this.email = this.overlay.querySelector('input[name="email"]')!;
    this.password = this.overlay.querySelector('input[name="password"]')!;
    this.firstName = this.overlay.querySelector('input[name="firstName"]')!;
    this.lastName = this.overlay.querySelector('input[name="lastName"]')!;
    this.company = this.overlay.querySelector('input[name="company"]')!;
    this.repeatPassword = this.overlay.querySelector('input[name="repeatPassword"]')!;
    this.registerFields = Array.from(this.overlay.querySelectorAll(".auth__register-field"));
    this.error = this.overlay.querySelector(".auth__error")!;
    this.submit = this.overlay.querySelector(".auth__submit")!;
    this.title = this.overlay.querySelector(".auth__title")!;
    this.back = this.overlay.querySelector(".auth__back")!;
    this.cancel = this.overlay.querySelector(".auth__cancel")!;
    this.hint = this.overlay.querySelector(".auth__hint")!;
    this.pendingPanel = this.overlay.querySelector(".auth__pending")!;
    this.pendingPanelMessage = this.overlay.querySelector(".auth__pending-message")!;
    this.pendingPanelClose = this.overlay.querySelector(".auth__pending-close")!;
    this.forgotLink = this.overlay.querySelector(".auth__forgot-link")!;
    this.forgotForm = this.overlay.querySelector(".auth__forgot")!;
    this.forgotEmail = this.overlay.querySelector('input[name="forgotEmail"]')!;
    this.forgotError = this.overlay.querySelector(".auth__forgot-error")!;
    this.forgotBack = this.overlay.querySelector(".auth__forgot-back")!;
    this.forgotCancel = this.overlay.querySelector(".auth__forgot-cancel")!;
    this.forgotSubmit = this.overlay.querySelector(".auth__forgot-submit")!;
    this.forgotSentPanel = this.overlay.querySelector(".auth__forgot-sent")!;
    this.forgotSentMessage = this.overlay.querySelector(".auth__forgot-sent-message")!;
    this.forgotSentClose = this.overlay.querySelector(".auth__forgot-sent-close")!;

    this.choicePanels.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-choice]");
      if (!button) return;
      // "Sign in" goes straight to the login form; "Create an account" hands
      // control back to the caller, which sends the visitor through a plan
      // picker before registration — this dialog only knows about identity,
      // not plans or payment.
      if (button.dataset["choice"] === "register") this.close("signup");
      else this.showForm("login", null, true);
    });
    this.choiceCancel.addEventListener("click", () => this.close(null));
    this.back.addEventListener("click", () => this.showChoice(this.pendingMessage));
    this.cancel.addEventListener("click", () => this.close(null));
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.attempt();
    });
    this.pendingPanelClose.addEventListener("click", () => this.close("pending-verification"));
    this.forgotLink.addEventListener("click", () => this.showForgot());
    this.forgotBack.addEventListener("click", () => this.showForm("login", null, this.formCameFromChoice));
    this.forgotCancel.addEventListener("click", () => this.close(null));
    this.forgotForm.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.attemptForgot();
    });
    this.forgotSentClose.addEventListener("click", () => this.close(null));

    // The backdrop means "no thanks", same as Cancel; a click that started
    // and ended on the card itself must not count; only where it began.
    this.overlay.addEventListener("mousedown", (event) => {
      if (this.dismissable && event.target === this.overlay) this.close(null);
    });
  }

  /**
   * Open, and resolve with the session once signed in, or null if dismissed.
   *
   * With no `mode`, the dialog opens on a choice between "Create an account"
   * and "Sign in" — `message` is shown as the prompt above both. Choosing
   * "Sign in" leads to that form as before; choosing "Create an account"
   * resolves immediately with `"signup"` instead of showing a form, since
   * this dialog only knows about identity — the caller is expected to run a
   * plan picker first, then call `open()` again with `mode: "register"` once
   * a plan is chosen (see `main.ts`'s `signInOrSignUp`/`registerForPlan`).
   *
   * Passing `mode` explicitly (e.g. once a plan is already chosen) skips the
   * choice screen and goes straight to that form, with `message` shown as a
   * status line above it — and can never resolve with `"signup"`, since
   * there is no choice screen to produce it from.
   *
   * `dismissable` (default true) controls whether Cancel, Escape and the
   * backdrop close the dialog. Pass `false` for the account-required gate at
   * startup and before opening a file — there is nothing to fall back to
   * without a session, so leaving it dismissable there just traded this
   * dialog for a dead-end screen behind it. The export-time re-auth prompt
   * (session expired mid-session, with results already on screen) is the
   * one caller that keeps the default: cancelling it correctly leaves the
   * user exactly where they were.
   */
  open(
    message: string | undefined,
    opts: { mode: "login"; dismissable?: boolean },
  ): Promise<Session | null>;
  open(
    message: string | undefined,
    opts: { mode: "register"; dismissable?: boolean },
  ): Promise<Session | "pending-verification" | null>;
  open(
    message?: string,
    opts?: { dismissable?: boolean },
  ): Promise<Session | "signup" | "pending-verification" | null>;
  open(
    message?: string,
    opts?: { mode?: "login" | "register"; dismissable?: boolean },
  ): Promise<Session | "signup" | "pending-verification" | null> {
    this.dismissable = opts?.dismissable ?? true;
    this.choiceCancel.hidden = !this.dismissable;
    this.choiceActions.hidden = !this.dismissable;
    this.cancel.hidden = !this.dismissable;

    this.pendingMessage = message ?? "Sign in to start splitting.";
    if (opts?.mode) {
      this.showForm(opts.mode, message ?? null, false);
    } else {
      this.showChoice(this.pendingMessage);
    }

    this.previouslyFocused = this.root.activeElement as HTMLElement | null;
    this.setBackgroundInert(true);
    this.overlay.hidden = false;
    this.root.addEventListener("keydown", this.handleKeydown, true);
    if (this.step === "choice") {
      this.choicePanels.querySelector<HTMLElement>("[data-choice]")?.focus();
    } else {
      this.email.focus();
    }

    return new Promise((resolve) => {
      this.resolve = resolve;
    });
  }

  /**
   * Escape and Tab, the two things a native `<dialog>` handles for free.
   * Bound once and attached/detached with the overlay's visibility, so it
   * never intercepts a keystroke while the form is closed.
   */
  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (this.dismissable) this.close(null);
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(
      this.card.querySelectorAll<HTMLElement>('button, input, a[href], [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => !el.hasAttribute("disabled"));
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;

    if (event.shiftKey && this.root.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && this.root.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** Everything outside the overlay stops being reachable while it's open. */
  private setBackgroundInert(inert: boolean): void {
    for (const child of Array.from(this.root.body.children)) {
      if (child !== this.overlay) (child as HTMLElement).inert = inert;
    }
  }

  /** Show the choice between creating an account and signing in. */
  private showChoice(message: string): void {
    this.step = "choice";
    this.title.textContent = message;
    this.hint.hidden = true;
    this.choice.hidden = false;
    this.form.hidden = true;
    this.pendingPanel.hidden = true;
    this.forgotForm.hidden = true;
    this.forgotSentPanel.hidden = true;
    this.showError(null);
  }

  /**
   * Show the login/register form directly. `cameFromChoice` controls whether
   * a "Back" link returns to the choice screen or is hidden — there is
   * nothing to go back to when a caller requested a specific mode up front.
   */
  private showForm(mode: "login" | "register", message: string | null, cameFromChoice: boolean): void {
    this.step = "form";
    this.formCameFromChoice = cameFromChoice;
    this.choice.hidden = true;
    this.form.hidden = false;
    this.pendingPanel.hidden = true;
    this.forgotForm.hidden = true;
    this.forgotSentPanel.hidden = true;
    this.hint.hidden = false;
    this.back.hidden = !cameFromChoice;
    this.setMode(mode);
    this.showError(message);
  }

  /** Show the "enter your email" step of the lost-password flow. */
  private showForgot(): void {
    this.step = "forgot";
    this.title.textContent = "Reset your password";
    this.choice.hidden = true;
    this.form.hidden = true;
    this.pendingPanel.hidden = true;
    this.forgotSentPanel.hidden = true;
    this.forgotForm.hidden = false;
    this.hint.hidden = true;
    this.forgotEmail.value = this.email.value;
    this.showForgotError(null);
    this.forgotEmail.focus();
  }

  /**
   * Shown after requesting a reset link. The message never says whether the
   * address is registered — same reasoning as showPending()/register(), and
   * why this ends the dialog on "Done" rather than returning to the form:
   * there is nothing further this dialog can usefully do once the request
   * has been sent.
   */
  private showForgotSent(email: string): void {
    this.step = "forgot-sent";
    this.title.textContent = "Check your email";
    this.forgotForm.hidden = true;
    this.forgotSentPanel.hidden = false;
    this.forgotSentMessage.textContent = email
      ? `If ${email} is registered, we've sent a link to reset your password.`
      : "If that address is registered, we've sent a link to reset your password.";
    this.forgotSentClose.focus();
  }

  /**
   * Shown after registering: the account exists but cannot sign in until the
   * verification link sent to `email` is clicked. There is nothing further to
   * submit here — no resend flow in v1 — so this ends the dialog on its one
   * "Done" action rather than returning to the form.
   */
  private showPending(email: string | undefined): void {
    this.step = "pending";
    this.title.textContent = "Check your email";
    this.hint.hidden = true;
    this.choice.hidden = true;
    this.form.hidden = true;
    this.forgotForm.hidden = true;
    this.forgotSentPanel.hidden = true;
    this.pendingPanel.hidden = false;
    this.pendingPanelMessage.textContent = email
      ? `We've sent a verification link to ${email}.`
      : "We've sent a verification link to your email address.";
    this.pendingPanelClose.focus();
  }

  private setMode(mode: "login" | "register"): void {
    this.mode = mode;
    const signingIn = mode === "login";

    this.title.textContent = signingIn ? "Sign in" : "Create an account";
    this.submit.textContent = signingIn ? "Sign in" : "Create account";
    this.hint.textContent = signingIn
      ? "Your drawing never leaves this machine."
      : "The free tier splits one nest, up to 100 parts.";
    this.password.autocomplete = signingIn ? "current-password" : "new-password";
    this.forgotLink.hidden = !signingIn;

    for (const field of this.registerFields) field.hidden = signingIn;
    this.firstName.required = !signingIn;
    this.lastName.required = !signingIn;
    this.repeatPassword.required = !signingIn;
  }

  private async attempt(): Promise<void> {
    const email = this.email.value.trim();
    const password = this.password.value;

    if (this.mode === "register" && password !== this.repeatPassword.value) {
      this.showError("Those passwords do not match.");
      return;
    }

    this.submit.disabled = true;
    this.showError(null);

    try {
      const session =
        this.mode === "login"
          ? await this.api.login(email, password)
          : await this.api.register(
              email,
              password,
              this.firstName.value.trim(),
              this.lastName.value.trim(),
              this.company.value.trim(),
            );
      if (this.mode === "register" && session.pendingVerification) {
        this.showPending(session.email);
      } else {
        this.close(session);
      }
    } catch (error) {
      // The server's message is shown verbatim. It is written to be safe to
      // show — it never says whether an address is registered — and rewording
      // it here would risk saying more than the server chose to.
      this.showError(
        error instanceof ApiError
          ? error.message
          : "Could not reach the server. Check your connection and try again.",
      );
    } finally {
      this.submit.disabled = false;
    }
  }

  private async attemptForgot(): Promise<void> {
    const email = this.forgotEmail.value.trim();

    this.forgotSubmit.disabled = true;
    this.showForgotError(null);

    try {
      await this.api.forgotPassword(email);
      this.showForgotSent(email);
    } catch (error) {
      this.showForgotError(
        error instanceof ApiError
          ? error.message
          : "Could not reach the server. Check your connection and try again.",
      );
    } finally {
      this.forgotSubmit.disabled = false;
    }
  }

  private showError(message: string | null): void {
    this.error.textContent = message ?? "";
    this.error.hidden = message === null;
  }

  private showForgotError(message: string | null): void {
    this.forgotError.textContent = message ?? "";
    this.forgotError.hidden = message === null;
  }

  private close(result: Session | "signup" | "pending-verification" | null): void {
    this.overlay.hidden = true;
    this.setBackgroundInert(false);
    this.root.removeEventListener("keydown", this.handleKeydown, true);
    this.password.value = "";
    this.repeatPassword.value = "";
    this.previouslyFocused?.focus();
    const resolve = this.resolve;
    this.resolve = null;
    resolve?.(result);
  }
}
